import { useEffect, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  Badge,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Input,
  Label,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@betstarters/ui'
import { ArrowLeft, Ban, Pencil, Plus, RotateCcw } from 'lucide-react'
import { api } from '../api'
import { useCapabilities } from '../hooks/useCapabilities'
import { useIntegrations, useReference } from '../hooks/useReference'
import { messageOf } from '../lib/apiError'
import { ErrorBanner, StatusBadge } from '../components/catalog'
import { Tab, TabCount, Tabs, TabsList, TabsPanel } from '../components/tabs'
import { Combobox } from '../components/controls'
import { Flag } from '../components/flag'
import { BankForm, emptyBankForm, type BankFormValue } from '../components/bankForm'
import type { Bank } from './Banks'

const TABS = ['summary', 'integrations'] as const
type TabValue = (typeof TABS)[number]

interface IntegrationLink {
  integrationId: number
  integration: string
  integrationType: string
  bankCode: string
  isDisabledByPlatform: boolean
  creationDate: string
  lastModificationDate: string | null
}

/**
 * BankDetail is one bank and the integrations that know it, split into tabs:
 * its own fields and the codes each integration calls it by
 * ([Catalog].[Platform.Bank_LK_Integration] — Chapa knows Abay Bank as "130").
 *
 * Two behaviours the schema dictates:
 *
 * - A pairing is DISABLED, never deleted. The row carries IsDisabledByPlatform
 *   and LastModificationDate, which is how this database models something with a
 *   lifecycle — unlike the PSP hub's bare assignments, which really are removed.
 * - A link whose integration no longer exists still shows, with its raw id.
 *   Production holds 24 of those on IntegrationID 0, and this page is the only
 *   place anyone would ever see them.
 */
export function BankDetailPage() {
  const { t } = useTranslation('app')
  const { id } = useParams()
  const qc = useQueryClient()
  const { has } = useCapabilities()
  const canWrite = has('catalog:write')
  const { countries } = useReference()
  const { integrations } = useIntegrations()

  const [searchParams, setSearchParams] = useSearchParams()
  const requested = searchParams.get('tab') as TabValue | null
  const tab: TabValue = requested && TABS.includes(requested) ? requested : 'summary'

  const [error, setError] = useState('')
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState<BankFormValue>(emptyBankForm)
  const [linkDialogOpen, setLinkDialogOpen] = useState(false)
  const [editingLink, setEditingLink] = useState<IntegrationLink | null>(null)
  const [linkIntegrationId, setLinkIntegrationId] = useState('')
  const [linkCode, setLinkCode] = useState('')

  const { data: bank } = useQuery({
    queryKey: ['banks', id],
    queryFn: async (): Promise<Bank> => {
      const res = await api.get(`/banks/${id}`)
      return res.data as Bank
    },
  })

  const { data: linkData } = useQuery({
    queryKey: ['banks', id, 'integrations'],
    queryFn: async (): Promise<{ integrations: IntegrationLink[] }> => {
      const res = await api.get(`/banks/${id}/integrations`)
      return res.data as { integrations: IntegrationLink[] }
    },
  })

  // Seed the edit form from the loaded bank (and re-seed after a save).
  useEffect(() => {
    if (!bank) return
    setForm({ bank: bank.bank, swift: bank.swift, countryId: String(bank.countryId) })
  }, [bank])

  const invalidate = () => qc.invalidateQueries({ queryKey: ['banks'] })
  const onError = (err: unknown) => setError(messageOf(err, t('banks.errors.save')))

  const save = useMutation({
    mutationFn: () =>
      api.put(`/banks/${id}`, {
        bank: form.bank.trim(),
        swift: form.swift.trim(),
        countryId: Number(form.countryId),
        isDisabledByPlatform: bank?.isDisabledByPlatform ?? false,
      }),
    onSuccess: () => {
      setEditing(false)
      setError('')
      invalidate()
    },
    onError,
  })

  const toggleDisabled = useMutation({
    mutationFn: () =>
      bank?.isDisabledByPlatform ? api.post(`/banks/${id}/restore`) : api.delete(`/banks/${id}`),
    onSuccess: () => {
      setError('')
      invalidate()
    },
    onError,
  })

  // One PUT covers add and edit: the pairing is keyed by (bank, integration),
  // so writing it twice updates the code instead of failing.
  const saveLink = useMutation({
    mutationFn: () =>
      api.put(`/banks/${id}/integrations/${linkIntegrationId}`, { bankCode: linkCode.trim() }),
    onSuccess: () => {
      setLinkDialogOpen(false)
      setError('')
      invalidate()
    },
    onError: (err) => setError(messageOf(err, t('banks.integrations.errors.save'))),
  })

  const toggleLink = useMutation({
    mutationFn: (l: IntegrationLink) =>
      l.isDisabledByPlatform
        ? api.post(`/banks/${id}/integrations/${l.integrationId}/restore`)
        : api.delete(`/banks/${id}/integrations/${l.integrationId}`),
    onSuccess: () => {
      setError('')
      invalidate()
    },
    onError: (err) => setError(messageOf(err, t('banks.integrations.errors.save'))),
  })

  const links = linkData?.integrations ?? []

  const openCreateLink = () => {
    setEditingLink(null)
    setLinkIntegrationId('')
    setLinkCode('')
    setError('')
    setLinkDialogOpen(true)
  }

  const openEditLink = (l: IntegrationLink) => {
    setEditingLink(l)
    setLinkIntegrationId(String(l.integrationId))
    setLinkCode(l.bankCode)
    setError('')
    setLinkDialogOpen(true)
  }

  if (!bank) {
    return <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
  }

  const formatDate = (value: string | null) => (value ? new Date(value).toLocaleString() : '—')

  // Only PSP integrations are offered — a bank code means nothing to a games or
  // a sportsbook integration — plus whatever is already linked, so an existing
  // pairing can always be re-saved.
  const linkedIds = new Set(links.map((l) => l.integrationId))
  const pickable = integrations.filter(
    (i) => i.integrationType === 'psp' || linkedIds.has(i.id),
  )

  return (
    <div>
      <Link
        to="/banks"
        className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-4" />
        {t('banks.title')}
      </Link>

      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold">{bank.bank}</h2>
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            {t('banks.columns.id')} {bank.bankId} ·
            {bank.country ? (
              <>
                <Flag region={bank.isoCode2} />
                {bank.isoCode2} {bank.country}
              </>
            ) : (
              `#${bank.countryId}`
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge disabled={bank.isDisabledByPlatform} />
          {canWrite && (
            <Button
              variant="outline"
              size="sm"
              disabled={toggleDisabled.isPending}
              onClick={() => toggleDisabled.mutate()}
            >
              {bank.isDisabledByPlatform ? <RotateCcw /> : <Ban />}
              {bank.isDisabledByPlatform ? t('common.restore') : t('common.retire')}
            </Button>
          )}
        </div>
      </div>

      <ErrorBanner message={error} />

      <Tabs
        value={tab}
        onValueChange={(value) => {
          searchParams.set('tab', String(value))
          setSearchParams(searchParams, { replace: true })
        }}
      >
        <TabsList>
          <Tab value="summary">{t('banks.tabs.summary')}</Tab>
          <Tab value="integrations">
            {t('banks.tabs.integrations')}
            <TabCount value={links.length} />
          </Tab>
        </TabsList>

        {/* ---- summary ---- */}
        <TabsPanel value="summary">
          {editing ? (
            <BankForm
              value={form}
              onChange={setForm}
              onSubmit={() => save.mutate()}
              onCancel={() => {
                setEditing(false)
                setError('')
              }}
              countries={countries}
              pending={save.isPending}
              error={error}
              submitLabel={t('common.save')}
            />
          ) : (
            <>
              <Card className="max-w-3xl">
                <CardContent>
                  <dl className="grid grid-cols-[14rem_1fr] gap-x-6 gap-y-3 text-sm">
                    <SummaryRow label={t('banks.columns.id')}>
                      <span className="font-mono text-xs">{bank.bankId}</span>
                    </SummaryRow>
                    <SummaryRow label={t('banks.columns.name')}>{bank.bank}</SummaryRow>
                    <SummaryRow label={t('banks.columns.swift')}>
                      <span className="font-mono text-xs">{bank.swift || '—'}</span>
                    </SummaryRow>
                    <SummaryRow label={t('banks.columns.country')}>
                      {bank.country ? (
                        <span className="flex items-center gap-2">
                          <Flag region={bank.isoCode2} />
                          <span className="font-mono text-xs">{bank.isoCode2}</span>
                          {bank.country}
                        </span>
                      ) : (
                        `#${bank.countryId}`
                      )}
                    </SummaryRow>
                    <SummaryRow label={t('common.columns.created')}>
                      {formatDate(bank.creationDate)}
                    </SummaryRow>
                    <SummaryRow label={t('paymentMethods.summary.lastModified')}>
                      {formatDate(bank.lastModificationDate)}
                    </SummaryRow>
                  </dl>
                </CardContent>
              </Card>

              {canWrite && (
                <Button className="mt-6" variant="outline" size="sm" onClick={() => setEditing(true)}>
                  <Pencil />
                  {t('common.edit')}
                </Button>
              )}
            </>
          )}
        </TabsPanel>

        {/* ---- integrations ---- */}
        <TabsPanel value="integrations">
          <p className="mb-3 max-w-3xl text-sm text-muted-foreground">
            {t('banks.integrations.intro')}
          </p>

          {canWrite && (
            <div className="mb-3 flex justify-end">
              <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
                <DialogTrigger
                  render={
                    <Button size="sm" onClick={openCreateLink}>
                      <Plus />
                      {t('banks.integrations.link')}
                    </Button>
                  }
                />
                <DialogContent className="sm:max-w-lg">
                  <DialogHeader>
                    <DialogTitle>
                      {editingLink
                        ? t('banks.integrations.editLink')
                        : t('banks.integrations.link')}
                    </DialogTitle>
                  </DialogHeader>
                  <form
                    className="flex flex-col gap-4"
                    onSubmit={(e) => {
                      e.preventDefault()
                      if (linkIntegrationId && linkCode.trim()) saveLink.mutate()
                    }}
                  >
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="link-integration">
                        {t('banks.integrations.columns.integration')}
                      </Label>
                      <Combobox
                        value={linkIntegrationId}
                        onValueChange={setLinkIntegrationId}
                        items={pickable.map((i) => ({ value: String(i.id), label: i.name }))}
                        placeholder={t('banks.integrations.pickIntegration')}
                        className="w-full"
                        disabled={editingLink !== null}
                      />
                      <p className="text-xs text-muted-foreground">
                        {t('banks.integrations.pickerHint')}
                      </p>
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="link-code">{t('banks.integrations.columns.code')}</Label>
                      <Input
                        id="link-code"
                        value={linkCode}
                        onChange={(e) => setLinkCode(e.target.value)}
                        maxLength={100}
                        className="font-mono"
                        placeholder="130"
                        autoFocus
                      />
                      <p className="text-xs text-muted-foreground">
                        {t('banks.integrations.codeHint')}
                      </p>
                    </div>

                    <ErrorBanner message={error} />

                    <div className="flex justify-end gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setLinkDialogOpen(false)}
                      >
                        {t('common.cancel')}
                      </Button>
                      <Button
                        type="submit"
                        disabled={saveLink.isPending || !linkIntegrationId || !linkCode.trim()}
                      >
                        {editingLink ? t('common.save') : t('common.create')}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          )}

          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead>{t('banks.integrations.columns.integration')}</TableHead>
                  <TableHead className="w-32">{t('banks.integrations.columns.type')}</TableHead>
                  <TableHead className="w-48">{t('banks.integrations.columns.code')}</TableHead>
                  <TableHead className="w-32">{t('common.columns.status')}</TableHead>
                  {canWrite && (
                    <TableHead className="w-28 text-right">{t('common.columns.actions')}</TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {links.map((l) => (
                  <TableRow key={l.integrationId}>
                    <TableCell className="font-medium">
                      {l.integration || (
                        <span className="text-muted-foreground">
                          {t('banks.integrations.unknown', { id: l.integrationId })}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      {l.integrationType ? (
                        <Badge variant="outline">{l.integrationType}</Badge>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-xs">{l.bankCode}</TableCell>
                    <TableCell>
                      <StatusBadge disabled={l.isDisabledByPlatform} />
                    </TableCell>
                    {canWrite && (
                      <TableCell>
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            title={t('common.edit')}
                            onClick={() => openEditLink(l)}
                          >
                            <Pencil />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            title={l.isDisabledByPlatform ? t('common.restore') : t('common.retire')}
                            disabled={toggleLink.isPending}
                            onClick={() => toggleLink.mutate(l)}
                          >
                            {l.isDisabledByPlatform ? <RotateCcw /> : <Ban />}
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {links.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={canWrite ? 5 : 4}
                      className="py-8 text-center text-muted-foreground"
                    >
                      {t('banks.integrations.empty')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </TabsPanel>
      </Tabs>
    </div>
  )
}

function SummaryRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd>{children}</dd>
    </>
  )
}
